import {COUNTRIES_CURRENCY, DISPATCH_COUNTRIES} from '../constants'
import {DISPATCH_COUNTRIES_MESSAGES} from '../pages/country-dispatch/constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getClosestLocale} from './utils'

/**
 * Retrieves the shipping country configuration based on the provided country code.
 * If no country code is provided, it returns the default shipping country configuration.
 *
 * @param {string} countryCode - The ISO code of the country to retrieve the configuration for.
 * @param {boolean} isUS - A flag indicating if the default country should be considered as the US.
 * @returns {Object} An object representing the shipping country configuration, containing:
 *                   - {string} id: The identifier of the country.
 *                   - {string} currency: The currency associated with the country.
 */
export const getShipToCountry = (countryCode, isUS) => {
    // if no countries are configured, return default country
    if (!countryCode) {
        const defaultShipToCountry = getDefaultShipToCountry(isUS)
        return {
            id: defaultShipToCountry.id,
            currency: defaultShipToCountry.currency
        }
    }
    const countries = COUNTRIES_CURRENCY
    const country = countries.filter((country) => country.id === countryCode)[0]
    // get country config
    return country
}

/**
 * Sorts the shipping countries by name.
 * @returns {code: string, name: string}[] - an array of sorted countries by name
 */
export const sortShipToCountries = (intl) => {
    const countryList = DISPATCH_COUNTRIES.flatMap((region) => region.countries)
    const translatedCountryList = countryList.map((country) => ({
        code: country.code,
        name: intl.formatMessage(
            DISPATCH_COUNTRIES_MESSAGES[country.name.toLowerCase().replace(/\s+/g, '_')]
        )
    }))
    const sortedList = translatedCountryList.toSorted((a, b) => a.name.localeCompare(b.name))

    return sortedList
}

/**
 * Retrieves the country name by its ISO code from the DISPATCH_COUNTRIES list.
 *
 * @param {string} countryCode - The ISO code of the country.
 * @returns {string|null} The name of the country, or null if not found.
 */
export const getCountryNameByCode = (countryCode) => {
    for (const continent of DISPATCH_COUNTRIES) {
        const country = continent.countries.find((country) => country.code === countryCode)
        if (country) {
            return country.name
        }
    }
    return null
}

/**
 * Resolves the country code from a given URL path.
 *
 * This function extracts the 'country' parameter from the query string of a given URL path.
 * It constructs a valid URL using a dummy domain to facilitate the extraction process.
 *
 * @param {string} path - The partial URL path containing query parameters.
 * @returns {string|null} - The country code extracted from the URL, or null if the 'country' parameter is not present.
 */
export const resolveCountryFromUrl = (path) => {
    // since url is a partial url, we pass in a dummy domain to create a validate url to pass into URL constructor
    const {search} = new URL(path, 'https://www.some-domain.com')
    const urlParams = new URLSearchParams(search)
    const country = urlParams.get('country')
    // can be country or null if it is not defined in url
    return country
}

/**
 * Determines the default shipping country and its associated currency based on the provided parameter.
 *
 * @param {boolean} isUS - A boolean flag indicating whether the default shipping country should be the United States.
 * @returns {Object} An object representing the default shipping country with the following properties:
 *   @property {string|null} id - The country code ('US' for United States, 'FR' for France).
 *   @property {string|null} currency - The currency code ('USD' for United States Dollar, 'EUR' for Euro).
 */
export const getDefaultShipToCountry = (isUS) => {
    const shipToCountry = {
        id: null,
        currency: null
    }

    if (isUS) {
        shipToCountry.id = 'US'
        shipToCountry.currency = 'USD'
    } else {
        shipToCountry.id = 'FR'
        shipToCountry.currency = 'EUR'
    }

    return shipToCountry
}

/**
 * Determines the appropriate language alias for redirecting a user based on their country code and current locale.
 *
 * @param {string} countryCode - The ISO country code (e.g., 'US', 'DE') used to determine the target site.
 * @param {string|null} currentLocale - The user's current locale (e.g., 'en-US', 'de-DE'), used to prioritize locale selection.
 * @returns {string} The language alias to use for redirection, based on the closest supported locale for the target site.
 */
export const getRedirectLanguage = (countryCode, currentLocale = null) => {
    const {app: appConfig} = getConfig()

    const targetSiteId = countryCode === 'US' ? 'VBQ-US' : 'VBQ-EU'
    const targetSite = appConfig?.sites.find((s) => s.id === targetSiteId)
    const browserLocales =
        typeof navigator !== 'undefined'
            ? navigator.languages || [navigator.language].filter(Boolean)
            : []

    // Get closest locale, prioritizing the currently selected locale
    const localeProps = getClosestLocale(targetSite, [
        ...(currentLocale ? [currentLocale] : []),
        ...browserLocales,
        ...targetSite.l10n.supportedLocales.map((locale) => locale.id)
    ])

    return localeProps.alias
}
